/**
 * Per-company feature flags ("funcții noi în testare") — copy in the three
 * operator languages.
 *
 * Bounded catalog, NOT registered in the application i18n (like
 * `i18n/paymentClaim.ts`): it follows the application locale at render time,
 * falling back to the saved localStorage `lang`, resolved to `ro | ru | en`
 * (anything else → `ro`). The backend sends RO label/description per key;
 * the Partner shows its own translation per key and falls back to the
 * backend text for an unknown key.
 */
import {computed, getCurrentInstance, unref, type ComputedRef} from "vue";
import type {CompanyFeatureKey} from "@/utils/companyFeatures";

export const COMPANY_FEATURES_LOCALES = ["ro", "ru", "en"] as const;
export type CompanyFeaturesLocale = typeof COMPANY_FEATURES_LOCALES[number];

export const resolveCompanyFeaturesLocale = (value: unknown): CompanyFeaturesLocale => {
  const code = String(value || "").toLowerCase().split(/[-_]/)[0];
  return (COMPANY_FEATURES_LOCALES as readonly string[]).includes(code) ? code as CompanyFeaturesLocale : "ro";
};

type FeatureCopy = {label: string; description: string};

const ro = {
  tab: "Funcții noi (în testare)",
  title: "Funcții noi (în testare)",
  intro: "Aceste funcții sunt încă în testare. Porniți-le doar pentru companiile care le încearcă; celelalte companii nu le văd deloc.",
  onlySuperAdmin: "Doar super-adminul Autocare poate porni sau opri aceste funcții.",
  statusOn: "Pornită",
  statusOff: "Oprită",
  statusOnGlobalOff: "Pornită, dar oprită global",
  unavailable: "Oprită global din configurația serverului: nu funcționează la nicio companie până nu este repornită acolo.",
  enabledBy: "Pornită de {name} la {date}",
  disabledBy: "Oprită de {name} la {date}",
  enabledAt: "Pornită la {date}",
  disabledAt: "Oprită la {date}",
  never: "Nu a fost pornită niciodată pentru această companie.",
  dirty: "Nesalvat",
  unsaved: "Modificări nesalvate: {count}",
  save: "Salvează",
  cancel: "Renunță",
  retry: "Reîncearcă",
  saved: "Setările funcțiilor noi au fost salvate.",
  stale: "Între timp altcineva a schimbat aceste setări. Am încărcat varianta actuală — verificați-o și refaceți modificarea, dacă mai este nevoie.",
  loadError: "Setările nu au putut fi încărcate.",
  saveError: "Setările nu au putut fi salvate. Modificările dvs. au rămas pe ecran — încercați din nou.",
  forbidden: "Doar super-adminul Autocare poate schimba aceste setări.",
  invalid: "Serverul a refuzat modificarea.",
  switchOn: "Pornește: {label}",
  switchOff: "Oprește: {label}",
  overviewTitle: "Unde sunt pornite acum",
  overviewIntro: "Toate companiile care au fiecare funcție pornită.",
  overviewCount: "Companii: {count}",
  overviewNone: "Nicio companie încă.",
  overviewGlobalOff: "Oprită global",
  overviewError: "Lista nu a putut fi încărcată.",
  overviewRefresh: "Actualizează",
  featureDisabled: "Funcția nu este activă pentru compania dvs.",
};

export type CompanyFeaturesMessageKey = keyof typeof ro;
type Catalog = Record<CompanyFeaturesMessageKey, string>;

const ru: Catalog = {
  tab: "Новые функции (тест)",
  title: "Новые функции (в тестировании)",
  intro: "Эти функции ещё тестируются. Включайте их только для компаний, которые их пробуют; остальные компании их вообще не видят.",
  onlySuperAdmin: "Включать и выключать эти функции может только супер-администратор Autocare.",
  statusOn: "Включена",
  statusOff: "Выключена",
  statusOnGlobalOff: "Включена, но выключена глобально",
  unavailable: "Выключена глобально в настройках сервера: не работает ни у одной компании, пока её там снова не включат.",
  enabledBy: "Включил(а) {name}, {date}",
  disabledBy: "Выключил(а) {name}, {date}",
  enabledAt: "Включена {date}",
  disabledAt: "Выключена {date}",
  never: "Для этой компании ещё ни разу не включалась.",
  dirty: "Не сохранено",
  unsaved: "Несохранённых изменений: {count}",
  save: "Сохранить",
  cancel: "Отменить",
  retry: "Повторить",
  saved: "Настройки новых функций сохранены.",
  stale: "Пока вы редактировали, кто-то другой изменил эти настройки. Мы загрузили актуальный вариант — проверьте его и при необходимости повторите изменение.",
  loadError: "Не удалось загрузить настройки.",
  saveError: "Не удалось сохранить настройки. Ваши изменения остались на экране — попробуйте ещё раз.",
  forbidden: "Менять эти настройки может только супер-администратор Autocare.",
  invalid: "Сервер отклонил изменение.",
  switchOn: "Включить: {label}",
  switchOff: "Выключить: {label}",
  overviewTitle: "Где включены сейчас",
  overviewIntro: "Все компании, у которых включена каждая функция.",
  overviewCount: "Компаний: {count}",
  overviewNone: "Пока ни одной компании.",
  overviewGlobalOff: "Выключена глобально",
  overviewError: "Не удалось загрузить список.",
  overviewRefresh: "Обновить",
  featureDisabled: "Эта функция не включена для вашей компании.",
};

const en: Catalog = {
  tab: "New features (testing)",
  title: "New features (in testing)",
  intro: "These features are still being tested. Turn them on only for the companies trying them out; other companies do not see them at all.",
  onlySuperAdmin: "Only the Autocare super administrator can turn these features on or off.",
  statusOn: "On",
  statusOff: "Off",
  statusOnGlobalOff: "On, but switched off globally",
  unavailable: "Switched off globally in the server configuration: it does not work for any company until it is switched back on there.",
  enabledBy: "Turned on by {name} on {date}",
  disabledBy: "Turned off by {name} on {date}",
  enabledAt: "Turned on on {date}",
  disabledAt: "Turned off on {date}",
  never: "Never turned on for this company.",
  dirty: "Unsaved",
  unsaved: "Unsaved changes: {count}",
  save: "Save",
  cancel: "Discard",
  retry: "Try again",
  saved: "New feature settings saved.",
  stale: "Someone else changed these settings in the meantime. We loaded the current version — check it and make your change again if still needed.",
  loadError: "The settings could not be loaded.",
  saveError: "The settings could not be saved. Your changes are still on screen — try again.",
  forbidden: "Only the Autocare super administrator can change these settings.",
  invalid: "The server refused the change.",
  switchOn: "Turn on: {label}",
  switchOff: "Turn off: {label}",
  overviewTitle: "Where they are on now",
  overviewIntro: "Every company that has each feature turned on.",
  overviewCount: "Companies: {count}",
  overviewNone: "No company yet.",
  overviewGlobalOff: "Switched off globally",
  overviewError: "The list could not be loaded.",
  overviewRefresh: "Refresh",
  featureDisabled: "This feature is not active for your company.",
};

export const COMPANY_FEATURES_MESSAGES: Record<CompanyFeaturesLocale, Catalog> = {ro, ru, en};

const FEATURES: Record<CompanyFeaturesLocale, Record<CompanyFeatureKey, FeatureCopy>> = {
  ro: {
    driver_cash_handover: {
      label: "Predarea cash-ului de către șofer",
      description: "Tabul Cash pe plecare: predarea numerarului către companie după terminarea cursei și istoricul corecțiilor.",
    },
    notification_self_service: {
      label: "Notificări automate editabile de companie",
      description: "Compania își pornește/oprește singură notificările către clienți pe canalele configurate de Autocare. SMS-urile se facturează companiei.",
    },
    driver_disruptions: {
      label: "Întârzieri din aplicația șoferului",
      description: "Șoferul poate declara o întârziere direct din aplicație. Întârzierile deja deschise se pot închide oricând.",
    },
    partner_disruptions: {
      label: "Întârzieri și incidente în Partener",
      description: "Panoul de întârzieri și incidente la cursele programate, plus anunțarea pasagerilor când o cursă este anulată sau pasagerii sunt transferați.",
    },
    settlements: {
      label: "Decontări între companii",
      description: "Extrase lunare, solduri, plăți și documente pentru decontările dintre companii.",
    },
    reports_analytics: {
      label: "Rapoarte și analiză",
      description: "Meniul Analiză: panoul zilei, vânzări, ocupare, încasări, punctualitate, calitate și rapoartele trimise automat pe email.",
    },
    booking_operations: {
      label: "Operațiuni rapide pe rezervări",
      description: "Acțiuni în masă pe rezervări (status, export, SMS), căutarea rapidă, șabloanele de comentarii și de mesaje și trimiterea programată a listei de pasageri pe email.",
    },
    promotions_pricing: {
      label: "Promoții și prețuri sezoniere",
      description: "Editorul de promoții și prețurile pe perioade (sezoane) din pagina rutei.",
    },
  },
  ru: {
    driver_cash_handover: {
      label: "Передача наличных водителем",
      description: "Вкладка наличных в отправлении: передача денег компании после завершения рейса и история исправлений.",
    },
    notification_self_service: {
      label: "Автоматические уведомления, редактируемые компанией",
      description: "Компания самостоятельно включает и выключает уведомления клиентам по каналам, настроенным Autocare. Отправленные SMS оплачивает компания.",
    },
    driver_disruptions: {
      label: "Задержки из приложения водителя",
      description: "Водитель может объявить задержку прямо из приложения. Уже открытые задержки можно закрыть в любой момент.",
    },
    partner_disruptions: {
      label: "Задержки и инциденты в Partener",
      description: "Панель задержек и инцидентов у рейсов, а также оповещение пассажиров при отмене рейса или переносе пассажиров.",
    },
    settlements: {
      label: "Взаиморасчёты между компаниями",
      description: "Ежемесячные выписки, остатки, платежи и документы для взаиморасчётов между компаниями.",
    },
    reports_analytics: {
      label: "Отчёты и аналитика",
      description: "Меню «Анализ»: сводка дня, продажи, заполняемость, выручка, пунктуальность, качество и отчёты, автоматически отправляемые по email.",
    },
    booking_operations: {
      label: "Быстрые операции с бронированиями",
      description: "Массовые действия с бронированиями (статус, экспорт, SMS), быстрый поиск, шаблоны комментариев и сообщений и отправка списка пассажиров по email по расписанию.",
    },
    promotions_pricing: {
      label: "Акции и сезонные цены",
      description: "Редактор акций и цены по периодам (сезонам) на странице маршрута.",
    },
  },
  en: {
    driver_cash_handover: {
      label: "Driver cash handover",
      description: "Cash tab on departures: handover to the company after finishing the trip, with correction history.",
    },
    notification_self_service: {
      label: "Company-editable automatic notifications",
      description: "The company turns its customer notifications on or off using channels configured by Autocare. Sent SMS messages are billed to the company.",
    },
    driver_disruptions: {
      label: "Delays from the driver app",
      description: "The driver can report a delay straight from the app. Delays that are already open can always be closed.",
    },
    partner_disruptions: {
      label: "Delays and incidents in Partener",
      description: "The delays and incidents panel on scheduled departures, plus notifying passengers when a departure is cancelled or passengers are transferred.",
    },
    settlements: {
      label: "Settlements between companies",
      description: "Monthly statements, balances, payments and documents for settlements between companies.",
    },
    reports_analytics: {
      label: "Reports and analytics",
      description: "The Analytics menu: today's dashboard, sales, occupancy, cash, punctuality, quality and the reports sent automatically by email.",
    },
    booking_operations: {
      label: "Quick booking operations",
      description: "Bulk actions on bookings (status, export, SMS), quick search, comment and message templates and the scheduled email delivery of the passenger list.",
    },
    promotions_pricing: {
      label: "Promotions and seasonal prices",
      description: "The promotions editor and the period (season) prices on the route page.",
    },
  },
};

export const translateCompanyFeatures = (
  locale: CompanyFeaturesLocale,
  key: CompanyFeaturesMessageKey,
  params: Record<string, string | number> = {},
): string => COMPANY_FEATURES_MESSAGES[locale][key].replace(/\{(\w+)\}/g, (match, name: string) =>
  Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : match);

/** Label + description of a feature key in `locale`; `null` for a key this build does not know. */
export const companyFeatureCopy = (locale: CompanyFeaturesLocale, key: string): FeatureCopy | null =>
  (FEATURES[locale] as Record<string, FeatureCopy>)[key] ?? null;

const DATE_LOCALES: Record<CompanyFeaturesLocale, string> = {ro: "ro-RO", ru: "ru-RU", en: "en-GB"};

/** A server timestamp in the viewer's local time (`30.09.2026, 14:05`); `null` when unreadable. */
export const formatCompanyFeatureDate = (value: string | null | undefined, locale: CompanyFeaturesLocale): string | null => {
  if (!value) return null;
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return null;
  return new Intl.DateTimeFormat(DATE_LOCALES[locale], {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(instant);
};

const storedLocale = (): string | null => {
  try {
    return typeof localStorage !== "undefined" ? localStorage.getItem("lang") : null;
  } catch (_) {
    return null;
  }
};

/** The UI locale outside a component (the stored choice). */
export const currentCompanyFeaturesLocale = (): CompanyFeaturesLocale => resolveCompanyFeaturesLocale(storedLocale());

/**
 * Must run inside `setup()`. Follows the application locale when the Vue
 * i18n plugin is installed, otherwise the saved `lang` preference.
 */
export const useCompanyFeaturesI18n = (): {
  locale: ComputedRef<CompanyFeaturesLocale>;
  t: (key: CompanyFeaturesMessageKey, params?: Record<string, string | number>) => string;
} => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const i18n: any = getCurrentInstance()?.appContext.config.globalProperties.$i18n;
  const stored = storedLocale();
  const locale = computed(() => resolveCompanyFeaturesLocale(i18n ? unref(i18n.locale) : stored));
  return {
    locale,
    t: (key, params = {}) => translateCompanyFeatures(locale.value, key, params),
  };
};
